import { useMemo } from "react"
import { combineArraysWithoutDuplicates } from "../components/GraphContainer/GraphContainer.utils"
import { selectRootCustomerId, selectSelectedVertices } from "../store/selectors"
import { useGraphStore } from "../store/store"
import type { Edge } from "../types/Edge"
import type { Vertex } from "../types/Vertex"
import { useGetCustomerNeighbors } from "./useGetCustomerNeighbors/useGetCustomerNeighbors"
import { useGetRelatedCustomers } from "./useGetRelatedCustomers"

export const useGraphData = () => {
  const rootCustomerId = useGraphStore(selectRootCustomerId)
  const selectedVertices = useGraphStore(selectSelectedVertices)

  const relatedCustomersQuery = useGetRelatedCustomers(rootCustomerId)
  const neighborsQuery = useGetCustomerNeighbors(selectedVertices)

  const isLoading = relatedCustomersQuery.isLoading || neighborsQuery.isFetching
  const isError = relatedCustomersQuery.isError || neighborsQuery.isError
  const error = relatedCustomersQuery.error ?? neighborsQuery.error

  const graphData = useMemo(() => {
    const areIdsSame = (a: { id: string }, b: { id: string }) => a.id === b.id

    // Enhanced edge comparison that prevents duplicate edges between same nodes
    // This solves the race condition where old and new parent edges temporarily coexist:
    // - During parent updates, queries may return inconsistent data (old edge vs new edge)
    // - ELK would render both edges, causing double-edge visualization
    // - This function ensures only one edge exists between any two nodes with the same label
    const areEdgesEquivalent = (a: Edge, b: Edge) =>
      areIdsSame(a, b) || (a.inV === b.inV && a.outV === b.outV && a.label === b.label)

    const relatedCustomers = relatedCustomersQuery.data?.data.relatedCustomers ?? []
    const neighborVertices = neighborsQuery.data.vertices
    const customerEdges = relatedCustomersQuery.data?.data.customerEdges ?? []
    const neighborEdges = neighborsQuery.data.edges

    return {
      nodes: combineArraysWithoutDuplicates<Vertex>([relatedCustomers, neighborVertices], areIdsSame),
      edges: combineArraysWithoutDuplicates<Edge>([customerEdges, neighborEdges], areEdgesEquivalent)
    }
  }, [relatedCustomersQuery.data, neighborsQuery.data])

  const refetch = () => {
    void relatedCustomersQuery.refetch()
  }

  return {
    graphData,
    isLoading,
    isError,
    error,
    refetch,
    relatedCustomersQuery,
    neighborsQuery
  }
}
